import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { ArrowRight, AlertTriangle, Cpu, Database, Brain, DollarSign, BookOpen, FileCheck, Award, Mail, Globe } from "lucide-react";

/* =========================================================================
   THERMAL ACADEMY · ELITE LANDING PAGE
   Apple keynote × Stripe homepage × Tesla minimalism × Academic gravitas.
   ========================================================================= */
export default function LandingPage() {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();

  const enterPlatform = () => navigate("/platform");

  return (
    <div className="ta-landing" style={{
      backgroundColor: "#0A0A0F",
      color: "#FFFFFF",
      minHeight: "100vh",
      overflowX: "hidden",
      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    }} data-testid="landing-root">

      {/* ─── Top Nav ──────────────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        backdropFilter: "blur(16px)",
        backgroundColor: "rgba(10,10,15,0.72)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        padding: "14px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontWeight: 800, letterSpacing: "0.04em", fontSize: 16, color: "#FFFFFF" }}>
            TA<span style={{ color: "#3B82F6" }}>·</span>THERMAL ACADEMY
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {/* Prominent inline nav links */}
          <a href="#landing-partner" data-testid="landing-nav-about"
            style={{
              color: "#94A3B8", textDecoration: "none",
              fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
              transition: "color 180ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#FFFFFF"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#94A3B8"; }}>
            {t("landing.nav.about", "ABOUT")}
          </a>
          <Link to="/whitepaper" data-testid="landing-nav-whitepaper"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              color: "#FBBF24", textDecoration: "none",
              fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
              padding: "5px 12px", borderRadius: 9999,
              border: "1px solid rgba(245,158,11,0.45)",
              backgroundColor: "rgba(245,158,11,0.08)",
              transition: "background-color 180ms ease, border-color 180ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(245,158,11,0.18)"; e.currentTarget.style.borderColor = "rgba(245,158,11,0.8)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(245,158,11,0.08)"; e.currentTarget.style.borderColor = "rgba(245,158,11,0.45)"; }}>
            <BookOpen size={11}/>
            {t("landing.nav.whitepaper", "WHITEPAPER")}
          </Link>
          <button onClick={() => setLang(lang === "en" ? "tr" : "en")}
            data-testid="landing-lang-toggle"
            style={{
              background: "none", border: "1px solid rgba(255,255,255,0.2)",
              color: "#FFFFFF", borderRadius: 9999, padding: "4px 12px",
              fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: "0.08em"
            }}>
            <Globe size={11} style={{ display: "inline", marginRight: 4, color: "#3B82F6" }}/>
            {lang.toUpperCase()}
          </button>
          <button onClick={enterPlatform} data-testid="landing-nav-cta"
            style={{
              background: "linear-gradient(135deg, #3B82F6, #06B6D4)",
              color: "#FFFFFF", border: "none", borderRadius: 9999,
              padding: "8px 18px", fontSize: 12, fontWeight: 700,
              cursor: "pointer", letterSpacing: "0.04em",
              boxShadow: "0 0 18px rgba(59,130,246,0.45)",
            }}>
            {t("landing.nav.cta", "Launch Platform")} →
          </button>
        </div>
      </nav>

      {/* ─── HERO ─────────────────────────────────────────────────────── */}
      <section style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center",
        padding: "120px 24px 60px",
        background: "radial-gradient(ellipse at center top, rgba(59,130,246,0.18) 0%, rgba(10,10,15,1) 60%)",
        position: "relative",
      }} data-testid="landing-hero">
        {/* Geometric grid overlay */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.08,
          backgroundImage: "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        }}/>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{
            display: "inline-block", padding: "4px 14px", borderRadius: 9999,
            backgroundColor: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.35)",
            color: "#3B82F6", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", marginBottom: 24,
          }}>
            ⚡ {t("landing.hero.eyebrow", "AI-POWERED · MULTI-STANDARD · CERTIFIED")}
          </div>
          <h1 style={{
            fontSize: "clamp(48px, 8vw, 110px)", fontWeight: 800,
            letterSpacing: "-0.04em", lineHeight: 0.95, marginBottom: 24,
            background: "linear-gradient(180deg, #FFFFFF 30%, #94A3B8 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            fontFamily: "'Playfair Display', Georgia, serif",
          }}>
            Thermal Academy
          </h1>
          <p style={{
            fontSize: "clamp(16px, 2vw, 22px)", maxWidth: 720, margin: "0 auto 40px",
            color: "#94A3B8", lineHeight: 1.5, fontWeight: 400,
          }}>
            {t("landing.hero.subtitle", "AI-Powered Smart Platform Giving Direction to the Future of Global Insulation")}
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={enterPlatform} data-testid="landing-hero-cta"
              style={{
                background: "linear-gradient(135deg, #3B82F6, #06B6D4)",
                color: "#FFFFFF", border: "none", borderRadius: 9999,
                padding: "14px 32px", fontSize: 15, fontWeight: 700, cursor: "pointer",
                letterSpacing: "0.02em", display: "inline-flex", alignItems: "center", gap: 8,
                boxShadow: "0 0 30px rgba(59,130,246,0.5)",
                transition: "transform 200ms ease, box-shadow 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 0 40px rgba(59,130,246,0.7)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 0 30px rgba(59,130,246,0.5)"; }}>
              {t("landing.hero.cta", "Launch TA Engine")} <ArrowRight size={16}/>
            </button>
          </div>

          {/* Domain Whisper Badge */}
          <div style={{ marginTop: 36, display: "flex", justifyContent: "center" }}>
            <a href="https://thermal-academy.ai" target="_blank" rel="noopener noreferrer"
              data-testid="landing-hero-domain"
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                fontSize: 11, fontWeight: 600, letterSpacing: "0.22em",
                color: "#64748B", textDecoration: "none",
                padding: "5px 14px", borderRadius: 9999,
                border: "1px solid rgba(255,255,255,0.06)",
                transition: "color 200ms ease, border-color 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#3B82F6"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.35)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#64748B"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#3B82F6", boxShadow: "0 0 8px rgba(59,130,246,0.7)" }}/>
              THERMAL-ACADEMY.AI
            </a>
          </div>
        </div>
      </section>

      {/* ─── PROBLEM ──────────────────────────────────────────────────── */}
      <section style={{ padding: "100px 24px", borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid="landing-problem">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <span style={{ color: "#EF4444", fontSize: 12, fontWeight: 700, letterSpacing: "0.2em" }}>
              {t("landing.problem.eyebrow", "THE CHALLENGE")}
            </span>
            <h2 style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 800, marginTop: 12, color: "#FFFFFF", fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em" }}>
              {t("landing.problem.title", "Fragmentation costs precision.")}
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
            {[
              { icon: AlertTriangle, color: "#EF4444", titleK: "landing.problem.c1.t", textK: "landing.problem.c1.b", titleD: "Fragmented Standards", textD: "TS 825, ISO 6946, ASTM C518, DIN 4108 — engineers juggle five incompatible specs daily." },
              { icon: AlertTriangle, color: "#F59E0B", titleK: "landing.problem.c2.t", textK: "landing.problem.c2.b", titleD: "Human Error", textD: "Manual calculation chains span 40+ steps, where a single typo cascades through entire reports." },
              { icon: AlertTriangle, color: "#FB923C", titleK: "landing.problem.c3.t", textK: "landing.problem.c3.b", titleD: "Theory-Practice Gap", textD: "No unified platform bridges scientific rigour with construction-site reality." },
            ].map((c, i) => (
              <div key={i} style={{
                backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 16, padding: 28,
              }}>
                <c.icon size={26} style={{ color: c.color, marginBottom: 16 }}/>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: "#FFFFFF" }}>{t(c.titleK, c.titleD)}</h3>
                <p style={{ fontSize: 14, color: "#94A3B8", lineHeight: 1.6 }}>{t(c.textK, c.textD)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SOLUTION · TA ENGINE ─────────────────────────────────────── */}
      <section style={{
        padding: "100px 24px",
        background: "linear-gradient(180deg, rgba(59,130,246,0.04) 0%, rgba(0,206,209,0.04) 100%)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }} data-testid="landing-solution">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <span style={{ color: "#3B82F6", fontSize: 12, fontWeight: 700, letterSpacing: "0.2em" }}>
              {t("landing.solution.eyebrow", "THE SOLUTION · TA ENGINE")}
            </span>
            <h2 style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 800, marginTop: 12, color: "#FFFFFF", fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em" }}>
              {t("landing.solution.title", "One platform. All standards. AI-driven precision.")}
            </h2>
            <p style={{ fontSize: 18, color: "#94A3B8", marginTop: 16, maxWidth: 680, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
              {t("landing.solution.subtitle", "Real-time thermal calculations powered by an intelligent analysis framework — multi-standard ready.")}
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 18, marginBottom: 50 }}>
            {[
              { icon: Cpu,      color: "#3B82F6", titleK: "landing.feature.1.t", textK: "landing.feature.1.b", titleD: "Calculation Engine",  textD: "High-performance core. R-value, U-value, condensation — instant." },
              { icon: Brain,    color: "#00CED1", titleK: "landing.feature.2.t", textK: "landing.feature.2.b", titleD: "Intelligent Analysis", textD: "Advanced AI-driven computation engine for adaptive material recommendations." },
              { icon: Database, color: "#A855F7", titleK: "landing.feature.3.t", textK: "landing.feature.3.b", titleD: "Material Database",   textD: "Curated R&D library — extensive material set with verified performance data." },
              { icon: DollarSign, color: "#FFCC00", titleK: "landing.feature.4.t", textK: "landing.feature.4.b", titleD: "Financial Analytics",textD: "Live FX, precious metals, energy savings, long-term ROI projections." },
            ].map((f, i) => (
              <div key={i} style={{
                backgroundColor: "rgba(255,255,255,0.03)", border: `1px solid ${f.color}33`,
                borderRadius: 14, padding: 22, transition: "transform 200ms ease, border-color 200ms ease",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.borderColor = f.color; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = `${f.color}33`; }}>
                <f.icon size={24} style={{ color: f.color, marginBottom: 12 }}/>
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: "#FFFFFF" }}>{t(f.titleK, f.titleD)}</h4>
                <p style={{ fontSize: 12.5, color: "#94A3B8", lineHeight: 1.55 }}>{t(f.textK, f.textD)}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <button onClick={enterPlatform} data-testid="landing-solution-cta"
              style={{
                background: "linear-gradient(135deg, #3B82F6, #06B6D4)",
                color: "#FFFFFF", border: "none", borderRadius: 9999,
                padding: "14px 36px", fontSize: 15, fontWeight: 700, cursor: "pointer",
                letterSpacing: "0.02em", display: "inline-flex", alignItems: "center", gap: 8,
                boxShadow: "0 0 30px rgba(59,130,246,0.5)",
              }}>
              {t("landing.solution.cta", "Access TA Engine")} <ArrowRight size={16}/>
            </button>
          </div>
        </div>
      </section>

      {/* ─── TECHNICAL WHITEPAPER ─────────────────────────────────────── */}
      <section style={{ padding: "100px 24px", borderTop: "1px solid rgba(255,255,255,0.06)", backgroundColor: "rgba(245,245,250,0.02)" }} data-testid="landing-constitution">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 50 }}>
            <span style={{ color: "#F59E0B", fontSize: 12, fontWeight: 700, letterSpacing: "0.22em" }}>
              {t("landing.constitution.eyebrow", "TECHNICAL WHITEPAPER · v1.0")}
            </span>
            <h2 style={{ fontSize: "clamp(26px, 4vw, 44px)", fontWeight: 800, marginTop: 14, color: "#FFFFFF", fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em", lineHeight: 1.15, maxWidth: 1000, marginLeft: "auto", marginRight: "auto" }}>
              {t("landing.constitution.title", "Intelligent Systems Integration in Thermal Engineering Analytics")}
            </h2>
            <h3 style={{ fontSize: "clamp(15px, 1.8vw, 20px)", fontWeight: 500, color: "#F59E0B", fontStyle: "italic", fontFamily: "'Playfair Display', Georgia, serif", marginTop: 14, maxWidth: 900, marginLeft: "auto", marginRight: "auto", lineHeight: 1.4 }}>
              {t("landing.constitution.subhead", "Multi-Standard Integration Framework for AI-Driven Building Physics")}
            </h3>
            <p style={{ fontSize: 14, color: "#94A3B8", marginTop: 18, maxWidth: 760, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
              {t("landing.constitution.subtitle", "13-chapter industry standard reference document.")}
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, maxWidth: 820, margin: "0 auto" }}>
            <Link to="/whitepaper" data-testid="landing-constitution-doc1"
              style={{
                background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.02))",
                border: "1px solid rgba(245,158,11,0.35)", borderRadius: 16, padding: 28,
                textDecoration: "none", color: "inherit",
                display: "flex", alignItems: "flex-start", gap: 20,
                transition: "transform 200ms ease, border-color 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = "#F59E0B"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(245,158,11,0.35)"; }}>
              <BookOpen size={26} style={{ color: "#F59E0B", flexShrink: 0, marginTop: 4 }}/>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 19, fontWeight: 700, marginBottom: 8, color: "#FFFFFF", lineHeight: 1.25 }}>
                  {t("landing.constitution.doc1.t", "Digital Constitution — Multi-Standard Framework")}
                </h3>
                <p style={{ fontSize: 13.5, color: "#CBD5E1", lineHeight: 1.65, marginBottom: 10 }}>
                  {t("landing.constitution.doc1.b", "The complete 13-chapter industry reference unifying TS 825:2024, ISO 6946, ASTM C518, DIN 4108/4102, and TS EN 13163 into a single traceable computational substrate.")}
                </p>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "#F59E0B", letterSpacing: "0.1em", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {t("landing.constitution.read", "READ FULL DOCUMENT")} <ArrowRight size={12}/>
                </span>
              </div>
            </Link>
            <Link to="/whitepaper#lambda" data-testid="landing-constitution-doc2"
              style={{
                background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.02))",
                border: "1px solid rgba(245,158,11,0.35)", borderRadius: 16, padding: 28,
                textDecoration: "none", color: "inherit",
                display: "flex", alignItems: "flex-start", gap: 20,
                transition: "transform 200ms ease, border-color 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = "#F59E0B"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(245,158,11,0.35)"; }}>
              <FileCheck size={26} style={{ color: "#F59E0B", flexShrink: 0, marginTop: 4 }}/>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 19, fontWeight: 700, marginBottom: 8, color: "#FFFFFF", lineHeight: 1.25 }}>
                  {t("landing.constitution.doc2.t", "AI Optimization — The Lambda-0.036 Protocol")}
                </h3>
                <p style={{ fontSize: 13.5, color: "#CBD5E1", lineHeight: 1.65, marginBottom: 10 }}>
                  {t("landing.constitution.doc2.b", "Architectural overview of the intelligent advisory layer and the cross-standard harmonization engine that guarantees regulatory traceability.")}
                </p>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "#F59E0B", letterSpacing: "0.1em", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {t("landing.constitution.read", "READ FULL DOCUMENT")} <ArrowRight size={12}/>
                </span>
              </div>
            </Link>
          </div>

          {/* Full Whitepaper CTA */}
          <div style={{ textAlign: "center", marginTop: 36 }}>
            <Link to="/whitepaper" data-testid="landing-constitution-cta"
              style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                background: "linear-gradient(135deg, #F59E0B, #FBBF24)",
                color: "#0A0A0F", textDecoration: "none", border: "none", borderRadius: 9999,
                padding: "14px 32px", fontSize: 15, fontWeight: 800,
                letterSpacing: "0.02em", boxShadow: "0 0 28px rgba(245,158,11,0.5)",
              }}>
              <BookOpen size={16}/>
              {t("landing.constitution.cta", "Read the Full Whitepaper")}
              <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FOUNDER PARTNER ELITE ────────────────────────────────────── */}
      <section style={{
        padding: "100px 24px",
        background: "radial-gradient(ellipse at center, rgba(245,158,11,0.08) 0%, rgba(10,10,15,1) 70%)",
        borderTop: "1px solid rgba(245,158,11,0.2)",
      }} data-testid="landing-partner">
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <div style={{
            display: "inline-block", padding: "5px 16px", borderRadius: 9999,
            background: "linear-gradient(135deg, #F59E0B, #FBBF24)", color: "#0A0A0F",
            fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", marginBottom: 24,
            boxShadow: "0 0 24px rgba(245,158,11,0.5)",
          }}>
            <Award size={11} style={{ display: "inline", marginRight: 4 }}/>
            {t("landing.partner.badge", "LIMITED OPPORTUNITY")}
          </div>
          <h2 style={{ fontSize: "clamp(36px, 6vw, 68px)", fontWeight: 800, color: "#FFFFFF", fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em", marginBottom: 16 }}>
            {t("landing.partner.title", "Founder Partner Elite")}
          </h2>
          <p style={{ fontSize: 18, color: "#FBBF24", fontWeight: 600, marginBottom: 28 }}>
            {t("landing.partner.subtitle", "Limited Strategic Partnership Opportunity")}
          </p>
          <div style={{
            background: "linear-gradient(135deg, rgba(245,158,11,0.06), rgba(255,255,255,0.02))",
            border: "1px solid rgba(245,158,11,0.45)", borderRadius: 20, padding: "36px 28px",
            marginBottom: 32, textAlign: "left",
          }}>
            <div style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 800, color: "#FBBF24", marginBottom: 24, textAlign: "center", letterSpacing: "-0.01em" }}>
              $50,000 {t("landing.partner.equity", "Strategic Partnership")}
            </div>
            <ul style={{ listStyle: "none", padding: 0, color: "#CBD5E1", fontSize: 14.5, lineHeight: 2 }}>
              <li>◆ {t("landing.partner.b1", "Shape the future of thermal engineering science")}</li>
              <li>◆ {t("landing.partner.b2", "Exclusive access to TA Engine enterprise features")}</li>
              <li>◆ {t("landing.partner.b3", "Co-development rights and priority integration")}</li>
              <li>◆ {t("landing.partner.b4", "Direct collaboration with Founder & Chief Scientist")}</li>
              <li>◆ {t("landing.partner.b5", "Limited to select industry leaders")}</li>
            </ul>
          </div>
          <a href="mailto:office@thermal-academy.ai?subject=Founder Partner Elite — Inquiry"
            data-testid="landing-partner-cta"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "linear-gradient(135deg, #F59E0B, #FBBF24)",
              color: "#0A0A0F", textDecoration: "none", border: "none", borderRadius: 9999,
              padding: "14px 32px", fontSize: 15, fontWeight: 800, cursor: "pointer",
              letterSpacing: "0.02em", boxShadow: "0 0 30px rgba(245,158,11,0.55)",
            }}>
            {t("landing.partner.cta", "Request Partnership Details")} <ArrowRight size={16}/>
          </a>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────────────── */}
      <footer style={{ padding: "60px 24px 40px", borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid="landing-footer">
        <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <h3 style={{ fontSize: 22, fontWeight: 700, color: "#FFFFFF", marginBottom: 4, fontFamily: "'Playfair Display', Georgia, serif" }}>
            Dr. Nihayet KOÇYİĞİT
          </h3>
          <p style={{ fontSize: 13, color: "#94A3B8", letterSpacing: "0.1em", marginBottom: 20, textTransform: "uppercase" }}>
            {t("landing.footer.title", "Founder & Chief Scientist")}
          </p>

          {/* Corporate Emails */}
          <div style={{ display: "flex", justifyContent: "center", gap: 24, flexWrap: "wrap", marginBottom: 28 }}>
            <a href="mailto:info@thermal-academy.ai" data-testid="landing-footer-email-info"
              style={{ color: "#3B82F6", fontSize: 14, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Mail size={14}/> info@thermal-academy.ai
            </a>
            <a href="mailto:office@thermal-academy.ai" data-testid="landing-footer-email-office"
              style={{ color: "#00CED1", fontSize: 14, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Mail size={14}/> office@thermal-academy.ai
            </a>
          </div>

          {/* Domain Badge */}
          <a href="https://thermal-academy.ai" target="_blank" rel="noopener noreferrer"
            data-testid="landing-footer-domain"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 18px", borderRadius: 9999,
              background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(0,206,209,0.08))",
              border: "1px solid rgba(59,130,246,0.35)",
              color: "#FFFFFF", textDecoration: "none",
              fontSize: 12.5, fontWeight: 700, letterSpacing: "0.12em",
              boxShadow: "0 0 18px rgba(59,130,246,0.15)",
            }}>
            <Globe size={12} style={{ color: "#3B82F6" }}/>
            www.thermal-academy.ai
          </a>

          <div style={{ marginTop: 36, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: 11.5, color: "#64748B" }}>
            © 2026 Thermal Academy. {t("landing.footer.rights", "All rights reserved.")}
          </div>
        </div>
      </footer>
    </div>
  );
}
